import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence, useAnimation, useScroll, useSpring, useTransform } from 'framer-motion';
import { format, addDays, subDays, parseISO, isWithinInterval, isToday, isYesterday, differenceInDays } from 'date-fns';
import { de } from 'date-fns/locale';
import localforage from 'localforage';
import { 
  Clock, Calendar, ChevronLeft, ChevronRight, Filter, UserCheck, UserX, 
  AlertTriangle, Bell, Download, Search, RefreshCw, Moon, Sun, Info, 
  CheckCircle2, XCircle, User, Users, Settings,
  AlertCircle, BellRing, Eye, FileBarChart, BarChart3, PieChart, List, Grid,
  ArrowDownToLine, FileSpreadsheet, Loader2, MoreHorizontal,
  Shield, Heart, Award,CalendarIcon,ChevronDown,X
} from 'lucide-react';

// Global constants
const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';
const LOGOUT_WINDOW_START = 19; // 7 PM
const LOGOUT_WINDOW_END = 24; // Midnight

// Status types for styling
const STATUS_TYPES = {
  ON_TIME: 'ON_TIME',
  LATE: 'LATE',
  MISSING: 'MISSING',
  EXEMPT: 'EXEMPT'
};

// Status colors with light and dark variants
const STATUS_COLORS = {
  [STATUS_TYPES.ON_TIME]: {
    main: '#10b981',  // Green
    light: '#ecfdf5',
    dark: '#059669',
    gradient: 'linear-gradient(135deg, #10b981, #059669)'
  },
  [STATUS_TYPES.LATE]: {
    main: '#f59e0b',  // Amber
    light: '#fffbeb',
    dark: '#d97706',
    gradient: 'linear-gradient(135deg, #f59e0b, #d97706)'
  },
  [STATUS_TYPES.MISSING]: {
    main: '#ef4444',  // Red
    light: '#fee2e2',
    dark: '#dc2626',
    gradient: 'linear-gradient(135deg, #ef4444, #dc2626)'
  },
  [STATUS_TYPES.EXEMPT]: {
    main: '#6366f1',  // Indigo
    light: '#eef2ff',
    dark: '#4f46e5',
    gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)'
  }
};

// Helper to get time from Date object or ISO string
const getTime = (date) => {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.getHours() * 60 + dateObj.getMinutes();
};

// Check if a time falls within the logout window
const isWithinLogoutWindow = (date) => {
  const time = getTime(date);
  if (!time) return false;
  const hours = Math.floor(time / 60);
  return hours >= LOGOUT_WINDOW_START && hours < LOGOUT_WINDOW_END;
};

// Calculate status based on logout time
const calculateStatus = (user, logoutDeadline) => {
  // If no status or actions, consider missing
  if (!user.lastLogoutTime) {
    return STATUS_TYPES.MISSING;
  }

  // Logic to determine if exempt
  if (user.exemptFromLogout) {
    return STATUS_TYPES.EXEMPT;
  }

  const logoutTime = new Date(user.lastLogoutTime);
  const deadlineTime = new Date(logoutDeadline); 

  // If logged out after deadline
  if (logoutTime > deadlineTime) {
    return STATUS_TYPES.LATE;
  }

  // If logged out on time
  return STATUS_TYPES.ON_TIME;
};

// Format time for display
const formatTime = (date) => {
  if (!date) return '–';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'HH:mm');
};

const DailySummaryDashboard = ({ onClose, campId }) => {
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [camps, setCamps] = useState([]);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [users, setUsers] = useState([]);
  const [processedUsers, setProcessedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [showSidebar, setShowSidebar] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [dateOffset, setDateOffset] = useState(0);
  const [logoutDeadline, setLogoutDeadline] = useState(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // For desktop
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // For mobile
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedView, setSelectedView] = useState('summary');
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [animationCompleted, setAnimationCompleted] = useState(false);
  
  // Refs for scroll animations
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const summaryRef = useRef(null);
  const userListRef = useRef(null);
  
  // Scroll animation controls
  const { scrollY } = useScroll({ container: containerRef });
  const smoothScrollY = useSpring(scrollY, { damping: 20, stiffness: 100 });
  
  // Transforms for parallax effects
  const headerOpacity = useTransform(smoothScrollY, [0, 100], [1, 0.8]);
  const headerScale = useTransform(smoothScrollY, [0, 100], [1, 0.95]);
  const headerY = useTransform(smoothScrollY, [0, 100], [0, -20]);
  
  // Animation controls for sequence
  const controlsHeader = useAnimation();
  const controlsSummary = useAnimation();
  const controlsUserList = useAnimation();
  
  // Stats calculations
  const stats = useMemo(() => {
    if (!processedUsers.length) return { onTime: 0, late: 0, missing: 0, exempt: 0, total: 0 };
    
    return processedUsers.reduce((acc, user) => {
      acc.total++;
      if (user.status === STATUS_TYPES.ON_TIME) acc.onTime++;
      else if (user.status === STATUS_TYPES.LATE) acc.late++;
      else if (user.status === STATUS_TYPES.MISSING) acc.missing++;
      else if (user.status === STATUS_TYPES.EXEMPT) acc.exempt++;
      return acc;
    }, { onTime: 0, late: 0, missing: 0, exempt: 0, total: 0 });
  }, [processedUsers]);
  
  // Filtered and sorted users
  const filteredUsers = useMemo(() => {
    return processedUsers
      .filter(user => {
        const matchesSearch = !searchQuery || 
          user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
        
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        const { key, direction } = sortConfig;
        
        // Special handling for status sort
        if (key === 'status') {
          const statusOrder = {
            [STATUS_TYPES.MISSING]: 0,
            [STATUS_TYPES.LATE]: 1,
            [STATUS_TYPES.ON_TIME]: 2,
            [STATUS_TYPES.EXEMPT]: 3
          };
          
          return direction === 'asc' 
            ? statusOrder[a.status] - statusOrder[b.status]
            : statusOrder[b.status] - statusOrder[a.status];
        }
        
        // Normal string comparison for other fields
        const compareA = a[key] || '';
        const compareB = b[key] || '';
        
        if (typeof compareA === 'string' && typeof compareB === 'string') {
          return direction === 'asc' 
            ? compareA.localeCompare(compareB)
            : compareB.localeCompare(compareA);
        }
        
        return direction === 'asc' 
          ? (compareA > compareB ? 1 : -1)
          : (compareB > compareA ? 1 : -1);
      });
  }, [processedUsers, searchQuery, statusFilter, sortConfig]);
  
  // Daily stats
  const dailyStats = useMemo(() => {
    const total = stats.total;
    if (!total) return [];
    
    return [
      { 
        title: 'Pünktlich abgemeldet', 
        count: stats.onTime, 
        percentage: Math.round((stats.onTime / total) * 100), 
        color: STATUS_COLORS[STATUS_TYPES.ON_TIME].main,
        icon: UserCheck,
        status: STATUS_TYPES.ON_TIME
      },
      { 
        title: 'Verspätet abgemeldet', 
        count: stats.late, 
        percentage: Math.round((stats.late / total) * 100), 
        color: STATUS_COLORS[STATUS_TYPES.LATE].main,
        icon: Clock,
        status: STATUS_TYPES.LATE
      },
      { 
        title: 'Nicht abgemeldet', 
        count: stats.missing, 
        percentage: Math.round((stats.missing / total) * 100), 
        color: STATUS_COLORS[STATUS_TYPES.MISSING].main,
        icon: UserX,
        status: STATUS_TYPES.MISSING
      },
      { 
        title: 'Ausgenommen', 
        count: stats.exempt, 
        percentage: Math.round((stats.exempt / total) * 100), 
        color: STATUS_COLORS[STATUS_TYPES.EXEMPT].main,
        icon: Shield,
        status: STATUS_TYPES.EXEMPT
      }
    ];
  }, [stats]);
  
  // Functions to fetch data
  const fetchCamps = async () => {
    try {
      setLoading(true);
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/camps`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to load camps');

      const data = await response.json();
      setCamps(data);
      
      // If campId is provided, select that camp
      if (campId) {
        const selectedCamp = data.find(camp => camp.id === campId);
        if (selectedCamp) {
          setSelectedCamp(selectedCamp);
          return;
        }
      }
      
      // Otherwise select first camp
      if (data && data.length > 0) {
        setSelectedCamp(data[0]);
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchDailyData = async (campId, date) => {
    try {
      setLoading(true);
      const token = await localforage.getItem('token');
      
      const formattedDate = format(date, 'yyyy-MM-dd');
      
      const response = await fetch(
        `${BASE_URL}/api/camps/${campId}/daily-summary`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            date: formattedDate,
            checkEndOfDay: true
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to load daily summary');
      }

      const data = await response.json();
      
      // Calculate logout deadline (7 PM on the selected date)
      const deadlineDate = new Date(date);
      deadlineDate.setHours(LOGOUT_WINDOW_START, 0, 0, 0);
      setLogoutDeadline(deadlineDate);
      
      // Process users data
      if (data.users && Array.isArray(data.users)) {
        setUsers(data.users);
        
        // Transform data for the UI
        const processed = data.users.map(user => {
          const userStatus = calculateStatus(user, deadlineDate);
          
          return {
            id: user.id || user.email,
            name: user.username || 'Unknown User',
            email: user.email || '',
            status: userStatus,
            lastLogoutTime: user.lastLogoutTime ? new Date(user.lastLogoutTime) : null,
            lastActivity: user.lastActivityTime ? new Date(user.lastActivityTime) : null,
            updatedBy: user.lastUpdatedBy || 'System',
            avatar: user.profileImagePath || null,
            room: user.room || '',
            group: user.group || '',
            notes: user.notes || '',
            statusHistory: user.statusHistory || []
          };
        });
        
        setProcessedUsers(processed);
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Track selected date with offset
  useEffect(() => {
    if (selectedCamp) {
      const newDate = new Date();
      newDate.setDate(newDate.getDate() + dateOffset);
      
      // Check if the new date is within camp dates
      const campStart = new Date(selectedCamp.startDate);
      const campEnd = new Date(selectedCamp.endDate);
      
      if (newDate >= campStart && newDate <= campEnd) {
        setSelectedDate(newDate);
      } else if (newDate < campStart) {
        setSelectedDate(campStart);
      } else if (newDate > campEnd) {
        setSelectedDate(campEnd);
      }
    }
  }, [dateOffset, selectedCamp]);
  
  // Initial data fetching
  useEffect(() => {
    fetchCamps();
  }, [campId]);
  
  // Fetch data when camp or date changes
  useEffect(() => {
    if (selectedCamp && selectedDate) {
      fetchDailyData(selectedCamp.id, selectedDate);
    }
  }, [selectedCamp, selectedDate]);
  
  // Handle sort requests
  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };
  
  // Track when page is fully loaded
  useEffect(() => {
    if (!loading) {
      // Short delay to ensure everything is rendered
      const timer = setTimeout(() => {
        setIsPageLoaded(true);
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [loading]);
  
  // Animation sequence
  useEffect(() => {
    if (isPageLoaded) {
      const sequence = async () => {
        await controlsHeader.start({ opacity: 1, y: 0, transition: { duration: 0.5 } });
        await controlsSummary.start({ opacity: 1, y: 0, transition: { duration: 0.4 } });
        await controlsUserList.start({ opacity: 1, y: 0, transition: { duration: 0.4 } });
        setAnimationCompleted(true);
      };
      
      sequence();
    }
  }, [isPageLoaded, controlsHeader, controlsSummary, controlsUserList]);
  
  // Format date with weekday
  const formatDateWithDay = (date) => {
    return format(date, 'eeee, dd. MMMM yyyy', { locale: de });
  };
  
  // Calculate relative date text (Today, Yesterday, etc.)
  const getRelativeDateText = (date) => {
    if (isToday(date)) return 'Heute';
    if (isYesterday(date)) return 'Gestern';
    
    const diff = differenceInDays(new Date(), date);
    if (diff > 0 && diff < 7) return `Vor ${diff} Tagen`;
    
    return '';
  };
  
  // Navigate to previous/next day
  const navigateDay = (direction) => {
    setDateOffset(prev => prev + (direction === 'next' ? 1 : -1));
  };
  
  // Reset to today
  const goToToday = () => {
    setDateOffset(0);
  };
  
  // Filter by status
  const filterByStatus = (status) => {
    setStatusFilter(status === statusFilter ? 'all' : status);
  };
  
  // Export data
  const exportData = () => {
    // Use exportFormat state to determine which format to use
    if (exportFormat === 'csv') {
      exportToCsv();
    } else {
      exportToExcel();
    }
    
    // Close export options menu
    setShowExportOptions(false);
  };
  
  const exportToCsv = () => {
    if (!filteredUsers.length) return;
    
    // Create CSV content
    const headers = ["Name", "Email", "Status", "Abmeldezeit", "Letzte Aktivität", "Raum", "Gruppe", "Notizen"];
    
    const rows = filteredUsers.map(user => [
      user.name,
      user.email,
      getStatusText(user.status),
      user.lastLogoutTime ? formatTime(user.lastLogoutTime) : '–',
      user.lastActivity ? formatTime(user.lastActivity) : '–',
      user.room || '–',
      user.group || '–',
      user.notes ? user.notes.replace(/"/g, '""') : '–'
    ]);
    
    // Format CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\\n');
    
    // Create file & trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Abmelde-Übersicht_${format(selectedDate, 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const exportToExcel = () => {
    // This function would use a library like xlsx or exceljs
    // For now we'll just alert that Excel export would happen here
    alert('Excel export functionality would go here - typically requires a library like xlsx');
    
    // For a real implementation:
    // 1. Import xlsx library
    // 2. Create a workbook and worksheet
    // 3. Add headers and data from filteredUsers
    // 4. Convert to blob and trigger download
  };
  
  // Get status text
  const getStatusText = (status) => {
    switch(status) {
      case STATUS_TYPES.ON_TIME:
        return 'Pünktlich';
      case STATUS_TYPES.LATE:
        return 'Verspätet';
      case STATUS_TYPES.MISSING:
        return 'Fehlend';
      case STATUS_TYPES.EXEMPT:
        return 'Ausgenommen';
      default:
        return 'Unbekannt';
    }
  };
  
  // Get display time based on status
  const getDisplayTime = (user) => {
    if (user.status === STATUS_TYPES.MISSING) {
      return 'Nicht abgemeldet';
    }
    
    return user.lastLogoutTime 
      ? formatTime(user.lastLogoutTime)
      : '–';
  };
  
  // Render user card
  const renderUserCard = (user) => {
    const statusColor = STATUS_COLORS[user.status];
    
    return (
      <motion.div 
        className="user-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ y: -3, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          borderLeft: `5px solid ${statusColor.main}`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: `radial-gradient(circle at top right, ${statusColor.light}88, transparent 70%)`,
            zIndex: 0
          }}
        />
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', zIndex: 1 }}>
          <div 
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: statusColor.light,
              color: statusColor.main,
              flexShrink: 0
            }}
          >
            {user.avatar ? (
              <img 
                src={user.avatar} 
                alt={user.name} 
                style={{ width: '100%', height: '100%', borderRadius: '12px', objectFit: 'cover' }}
              />
            ) : (
              <User size={24} />
            )}
          </div>
          
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: 600, 
              margin: 0, 
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: '#1e293b'
            }}>
              {user.name}
            </h3>
            <p style={{ 
              fontSize: '14px', 
              color: '#64748b', 
              margin: '3px 0 0 0',
              whiteSpace: 'nowrap', 
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {user.email}
            </p>
          </div>
        </div>
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          padding: '12px',
          backgroundColor: statusColor.light,
          borderRadius: '10px',
          zIndex: 1
        }}>
          <div style={{ width: '20px', color: statusColor.main }}>
            <Clock size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              Abmeldung
            </div>
            <div style={{ fontSize: '16px', fontWeight: 600, color: statusColor.main }}>
              {getDisplayTime(user)}
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', zIndex: 1 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontSize: '13px',
            color: '#64748b'
          }}>
            <BellRing size={14} />
            <span>Frist: {formatTime(logoutDeadline)}</span>
          </div>
          
          <div style={{ 
            flex: 1, 
            textAlign: 'right',
            padding: '4px 12px',
            backgroundColor: statusColor.light,
            borderRadius: '999px',
            fontSize: '13px',
            fontWeight: 600,
            color: statusColor.main,
            display: 'inline-block'
          }}>
            {getStatusText(user.status)}
          </div>
        </div>
      </motion.div>
    );
  };
  
  // Render user row for table view
  const renderUserRow = (user, index) => {
    const statusColor = STATUS_COLORS[user.status];
    
    return (
      <motion.tr 
        key={user.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: index * 0.05 }}
        style={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #f1f5f9'
        }}
        whileHover={{ backgroundColor: '#f8fafc' }}
      >
        <td style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div 
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: statusColor.light,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: statusColor.main
            }}
          >
            {user.avatar ? (
              <img 
                src={user.avatar} 
                alt={user.name} 
                style={{ width: '100%', height: '100%', borderRadius: '8px', objectFit: 'cover' }}
              />
            ) : (
              <User size={18} />
            )}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#1e293b' }}>{user.name}</div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>{user.email}</div>
          </div>
        </td>
        <td style={{ padding: '12px' }}>
          <span 
            style={{
              display: 'inline-block',
              padding: '4px 10px',
              borderRadius: '999px',
              backgroundColor: statusColor.light,
              color: statusColor.main,
              fontSize: '13px',
              fontWeight: 600
            }}
          >
            {getStatusText(user.status)}
          </span>
        </td>
        <td style={{ padding: '12px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '6px',
            fontWeight: user.status === STATUS_TYPES.MISSING ? 600 : 400,
            color: user.status === STATUS_TYPES.MISSING ? statusColor.main : '#1e293b'
          }}>
            <Clock size={16} />
            {getDisplayTime(user)}
          </div>
        </td>
        <td style={{ padding: '12px', color: '#64748b', fontSize: '13px' }}>
          {user.lastActivity ? formatTime(user.lastActivity) : '–'}
        </td>
        <td style={{ padding: '12px', color: '#64748b' }}>
          {user.room || '–'}
        </td>
        <td style={{ padding: '12px', color: '#64748b' }}>
          {user.notes || '–'}
        </td>
      </motion.tr>
    );
  };
  
  // Main grid layout with beautiful Apple-inspired design
  return (
    <motion.div
      className="daily-summary-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
        color: isDarkMode ? '#f1f5f9' : '#1e293b',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        display: 'flex',
        zIndex: 1000,
        overflow: 'hidden'
      }}
    >
      {/* Sidebar for desktop */}
      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div
            className="sidebar"
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{
              width: '300px',
              height: '100%',
              backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
              borderRight: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.05)',
              zIndex: 10
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ 
                fontSize: '18px', 
                fontWeight: 600, 
                margin: 0, 
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Calendar size={22} color="#3b82f6" /> 
                Tagesübersicht
              </h2>
              <button
                onClick={() => setIsSidebarOpen(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: isDarkMode ? '#64748b' : '#94a3b8',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <ChevronLeft size={20} />
              </button>
            </div>
            
            {/* Camp Selection */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: isDarkMode ? '#94a3b8' : '#64748b',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Lager
              </h3>
              
              <div style={{ 
                maxHeight: '200px', 
                overflowY: 'auto', 
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                scrollbarWidth: 'thin',
                scrollbarColor: isDarkMode ? '#475569 #1e293b' : '#cbd5e1 #ffffff'
              }}>
                {camps.map(camp => (
                  <motion.button
                    key={camp.id}
                    onClick={() => setSelectedCamp(camp)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      padding: '12px 14px',
                      backgroundColor: selectedCamp?.id === camp.id 
                        ? (isDarkMode ? '#3b82f620' : '#3b82f610')
                        : 'transparent',
                      color: selectedCamp?.id === camp.id 
                        ? '#3b82f6'
                        : isDarkMode ? '#f1f5f9' : '#1e293b',
                      border: `1px solid ${selectedCamp?.id === camp.id 
                        ? '#3b82f6' 
                        : isDarkMode ? '#334155' : '#e2e8f0'}`,
                      borderRadius: '10px',
                      textAlign: 'left',
                      fontSize: '14px',
                      fontWeight: selectedCamp?.id === camp.id ? 600 : 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ 
                      backgroundColor: selectedCamp?.id === camp.id ? '#3b82f615' : isDarkMode ? '#334155' : '#f1f5f9',
                      color: selectedCamp?.id === camp.id ? '#3b82f6' : isDarkMode ? '#94a3b8' : '#64748b',
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Users size={16} />
                    </div>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {camp.title}
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>
            
            {/* Date Navigation */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: isDarkMode ? '#94a3b8' : '#64748b',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Datum
              </h3>
              
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <motion.button
                  onClick={() => navigateDay('prev')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                    border: 'none',
                    borderRadius: '10px',
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                >
                  <ChevronLeft size={18} />
                </motion.button>
                
                <motion.button
                  onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                    border: 'none',
                    borderRadius: '10px',
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CalendarIcon size={16} />
                    <span>{format(selectedDate, 'dd.MM.yyyy')}</span>
                  </div>
                  <ChevronDown size={16} />
                </motion.button>
                
                <motion.button
                  onClick={() => navigateDay('next')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                    border: 'none',
                    borderRadius: '10px',
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                >
                  <ChevronRight size={18} />
                </motion.button>
              </div>
              
              <motion.button
                onClick={goToToday}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  backgroundColor: '#3b82f6',
                  border: 'none',
                  borderRadius: '10px',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                <Calendar size={16} />
                <span>Heutiges Datum</span>
              </motion.button>
            </div>
            
            {/* Status Filters */}
            <div>
              <h3 style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: isDarkMode ? '#94a3b8' : '#64748b',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Status Filter
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.values(STATUS_TYPES).map(status => {
                  const color = STATUS_COLORS[status];
                  const isActive = statusFilter === status;
                  
                  return (
                    <motion.button
                      key={status}
                      onClick={() => filterByStatus(status)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        padding: '12px 14px',
                        backgroundColor: isActive 
                          ? (isDarkMode ? `${color.main}20` : `${color.main}10`)
                          : isDarkMode ? '#334155' : '#f1f5f9',
                        border: `1px solid ${isActive ? color.main : 'transparent'}`,
                        borderRadius: '10px',
                        color: isActive ? color.main : (isDarkMode ? '#f1f5f9' : '#1e293b'),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: isActive ? 600 : 400
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ 
                          width: '20px', 
                          height: '20px', 
                          borderRadius: '6px',
                          backgroundColor: color.light,
                          color: color.main,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {status === STATUS_TYPES.ON_TIME && <CheckCircle2 size={14} />}
                          {status === STATUS_TYPES.LATE && <Clock size={14} />}
                          {status === STATUS_TYPES.MISSING && <XCircle size={14} />}
                          {status === STATUS_TYPES.EXEMPT && <Shield size={14} />}
                        </div>
                        <span>{getStatusText(status)}</span>
                      </div>
                      
                      <span style={{ 
                        backgroundColor: isActive ? color.light : isDarkMode ? '#475569' : '#e2e8f0',
                        color: isActive ? color.main : isDarkMode ? '#cbd5e1' : '#64748b',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        minWidth: '26px',
                        textAlign: 'center'
                      }}>
                        {status === STATUS_TYPES.ON_TIME && stats.onTime}
                        {status === STATUS_TYPES.LATE && stats.late}
                        {status === STATUS_TYPES.MISSING && stats.missing}
                        {status === STATUS_TYPES.EXEMPT && stats.exempt}
                      </span>
                    </motion.button>
                  );
                })}
                
                <motion.button
                  onClick={() => setStatusFilter('all')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    padding: '12px 14px',
                    backgroundColor: statusFilter === 'all' 
                      ? (isDarkMode ? '#3b82f620' : '#3b82f610')
                      : isDarkMode ? '#334155' : '#f1f5f9',
                    border: `1px solid ${statusFilter === 'all' ? '#3b82f6' : 'transparent'}`,
                    borderRadius: '10px',
                    color: statusFilter === 'all' ? '#3b82f6' : (isDarkMode ? '#f1f5f9' : '#1e293b'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: statusFilter === 'all' ? 600 : 400
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ 
                      width: '20px', 
                      height: '20px', 
                      borderRadius: '6px',
                      backgroundColor: statusFilter === 'all' ? '#3b82f615' : isDarkMode ? '#475569' : '#e2e8f0',
                      color: statusFilter === 'all' ? '#3b82f6' : isDarkMode ? '#cbd5e1' : '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Users size={14} />
                    </div>
                    <span>Alle anzeigen</span>
                  </div>
                  
                  <span style={{ 
                    backgroundColor: statusFilter === 'all' ? '#3b82f615' : isDarkMode ? '#475569' : '#e2e8f0',
                    color: statusFilter === 'all' ? '#3b82f6' : isDarkMode ? '#cbd5e1' : '#64748b',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '2px 8px',
                    minWidth: '26px',
                    textAlign: 'center'
                  }}>
                    {stats.total}
                  </span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Main content area with Apple-style scrolling */}
      <div 
        ref={containerRef}
        style={{ 
          flex: 1,
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollBehavior: 'smooth',
          position: 'relative'
        }}
      >
        {/* Header with controls that stays visible with parallax effect */}
        <motion.div
          ref={headerRef}
          animate={controlsHeader}
          initial={{ opacity: 0, y: -20 }}
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            padding: '20px 24px',
            backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
            borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
            backdropFilter: 'blur(10px)',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
            opacity: headerOpacity,
            scale: headerScale,
            y: headerY
          }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {!isSidebarOpen && (
                <motion.button
                  onClick={() => setIsSidebarOpen(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                    border: 'none',
                    borderRadius: '10px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <ChevronRight size={20} />
                </motion.button>
              )}
              
              <div>
                <motion.h1 
                  style={{ 
                    fontSize: '20px', 
                    fontWeight: 700, 
                    margin: 0,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b'
                  }}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  Tagesübersicht
                </motion.h1>
                <motion.p 
                  style={{ 
                    fontSize: '14px', 
                    color: isDarkMode ? '#94a3b8' : '#64748b', 
                    margin: '4px 0 0 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                >
                  <Calendar size={14} />
                  <span>{formatDateWithDay(selectedDate)}</span>
                  {getRelativeDateText(selectedDate) && (
                    <span style={{ 
                      backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '12px',
                      fontWeight: 500
                    }}>
                      {getRelativeDateText(selectedDate)}
                    </span>
                  )}
                </motion.p>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <motion.button
                onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                {viewMode === 'grid' ? <List size={18} /> : <Grid size={18} />}
                <span>{viewMode === 'grid' ? 'Tabelle' : 'Karten'}</span>
              </motion.button>
              
              <motion.div style={{ position: 'relative' }}>
                <motion.button
                  onClick={() => setShowExportOptions(!showExportOptions)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    backgroundColor: '#3b82f6',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  <Download size={18} />
                  <span>Exportieren</span>
                </motion.button>
                
                <AnimatePresence>
                  {showExportOptions && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      style={{
                        position: 'absolute',
                        top: '44px',
                        right: 0,
                        zIndex: 20,
                        backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        width: '180px',
                        overflow: 'hidden'
                      }}
                    >
                      <motion.button
                        onClick={() => {
                          setExportFormat('csv');
                          exportData();
                        }}
                        whileHover={{ backgroundColor: isDarkMode ? '#334155' : '#f1f5f9' }}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                          textAlign: 'left',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        <ArrowDownToLine size={16} />
                        <span>Als CSV exportieren</span>
                      </motion.button>
                      
                      <motion.button
                        onClick={() => {
                          setExportFormat('excel');
                          exportData();
                        }}
                        whileHover={{ backgroundColor: isDarkMode ? '#334155' : '#f1f5f9' }}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: isDarkMode ? '#f1f5f9' : '#1e293b',
                          textAlign: 'left',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        <FileSpreadsheet size={16} />
                        <span>Als Excel exportieren</span>
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
              
              <motion.button
                onClick={() => setIsDarkMode(!isDarkMode)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                  border: 'none',
                  borderRadius: '10px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: isDarkMode ? '#f1f5f9' : '#1e293b'
                }}
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </motion.button>
              
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                  border: 'none',
                  borderRadius: '10px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: isDarkMode ? '#f1f5f9' : '#1e293b'
                }}
              >
                <X size={20} />
              </motion.button>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: '12px',
            marginTop: '16px'
          }}>
            <div style={{ 
              position: 'relative',
              flex: 1
            }}>
              <Search 
                size={20} 
                style={{ 
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: isDarkMode ? '#64748b' : '#94a3b8',
                  pointerEvents: 'none'
                }}
              />
              <input
                type="text"
                placeholder="Teilnehmer suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 48px',
                  backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)'
                }}
              />
            </div>
            
            <motion.button
              onClick={() => fetchDailyData(selectedCamp.id, selectedDate)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                border: 'none',
                borderRadius: '12px',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: isDarkMode ? '#f1f5f9' : '#1e293b'
              }}
            >
              <RefreshCw size={20} />
            </motion.button>
          </div>
        </motion.div>
        
        {/* Summary Stats */}
        <motion.div
          ref={summaryRef}
          animate={controlsSummary}
          initial={{ opacity: 0, y: 20 }}
          style={{
            padding: '24px',
            marginBottom: '20px'
          }}
        >
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px'
          }}>
            {dailyStats.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                whileHover={{ 
                  y: -5, 
                  boxShadow: `0 10px 25px -5px ${stat.color}20`
                }}
                style={{
                  backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                  borderRadius: '16px',
                  padding: '20px',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden',
                  border: `1px solid ${isDarkMode ? '#334155' : '#f1f5f9'}`
                }}
                onClick={() => filterByStatus(stat.status)}
              >
                <div style={{ 
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '150px',
                  height: '150px',
                  background: `radial-gradient(circle at top right, ${stat.color}10, transparent 70%)`,
                  pointerEvents: 'none'
                }} />
                
                <div style={{ 
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: `${stat.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}>
                  <stat.icon size={24} color={stat.color} />
                </div>
                
                <div style={{ 
                  fontSize: '28px',
                  fontWeight: 700,
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '8px'
                }}>
                  {stat.count}
                  <span style={{ 
                    fontSize: '14px',
                    fontWeight: 500,
                    color: isDarkMode ? '#94a3b8' : '#64748b',
                    paddingBottom: '4px'
                  }}>
                    ({stat.percentage}%)
                  </span>
                </div>
                
                <div style={{ 
                  fontSize: '15px',
                  color: isDarkMode ? '#94a3b8' : '#64748b'
                }}>
                  {stat.title}
                </div>
                
                {statusFilter === stat.status && (
                  <div style={{ 
                    display: 'inline-block',
                    marginTop: '12px',
                    padding: '4px 10px',
                    backgroundColor: `${stat.color}15`,
                    color: stat.color,
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 500
                  }}>
                    Aktiver Filter
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
        
        {/* Users list */}
        <motion.div
          ref={userListRef}
          animate={controlsUserList}
          initial={{ opacity: 0, y: 20 }}
          style={{
            padding: '0 24px 24px 24px'
          }}
        >
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <h2 style={{ 
              fontSize: '18px', 
              fontWeight: 600, 
              margin: 0,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Users size={20} />
              Teilnehmer ({filteredUsers.length})
            </h2>
            
            <div style={{ 
              backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
              borderRadius: '8px',
              padding: '4px',
              display: 'flex',
              gap: '4px'
            }}>
              <button
                onClick={() => setViewMode('grid')}
                style={{
                  backgroundColor: viewMode === 'grid' ? (isDarkMode ? '#1e293b' : '#ffffff') : 'transparent',
                  color: viewMode === 'grid' ? (isDarkMode ? '#f1f5f9' : '#1e293b') : (isDarkMode ? '#94a3b8' : '#64748b'),
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer'
                }}
              >
                <Grid size={16} />
                <span>Karten</span>
              </button>
              
              <button
                onClick={() => setViewMode('table')}
                style={{
                  backgroundColor: viewMode === 'table' ? (isDarkMode ? '#1e293b' : '#ffffff') : 'transparent',
                  color: viewMode === 'table' ? (isDarkMode ? '#f1f5f9' : '#1e293b') : (isDarkMode ? '#94a3b8' : '#64748b'),
                  border: 'none',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer'
                }}
              >
                <List size={16} />
                <span>Tabelle</span>
              </button>
            </div>
          </div>
          
          {/* Loading state */}
          {loading && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '60px 0',
              color: isDarkMode ? '#94a3b8' : '#64748b'
            }}>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                style={{ marginBottom: '16px' }}
              >
                <Loader2 size={32} />
              </motion.div>
              <p style={{ fontWeight: 500 }}>Daten werden geladen...</p>
            </div>
          )}
          
          {/* Empty state */}
          {!loading && filteredUsers.length === 0 && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '60px 0',
              color: isDarkMode ? '#94a3b8' : '#64748b',
              backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
            }}>
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
                style={{ 
                  width: '80px', 
                  height: '80px',
                  borderRadius: '24px',
                  backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}
              >
                <Users size={40} color={isDarkMode ? '#94a3b8' : '#64748b'} />
              </motion.div>
              <h3 style={{ 
                margin: '0 0 8px 0',
                fontWeight: 600,
                fontSize: '18px',
                color: isDarkMode ? '#f1f5f9' : '#1e293b'
              }}>
                Keine Teilnehmer gefunden
              </h3>
              <p style={{ 
                margin: '0 0 20px 0',
                maxWidth: '400px',
                textAlign: 'center',
                fontSize: '15px'
              }}>
                {searchQuery 
                  ? `Keine Ergebnisse für "${searchQuery}". Versuche einen anderen Suchbegriff.`
                  : statusFilter !== 'all'
                    ? `Keine Teilnehmer mit dem Status "${getStatusText(statusFilter)}".`
                    : 'Für den ausgewählten Tag wurden keine Teilnehmer gefunden.'}
              </p>
              
              {(searchQuery || statusFilter !== 'all') && (
                <motion.button
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    backgroundColor: '#3b82f6',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '10px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  <RefreshCw size={16} />
                  <span>Filter zurücksetzen</span>
                </motion.button>
              )}
            </div>
          )}
          
          {/* Grid view */}
          {!loading && filteredUsers.length > 0 && viewMode === 'grid' && (
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '16px'
            }}>
              {filteredUsers.map((user, index) => renderUserCard(user))}
            </div>
          )}
          
          {/* Table view */}
          {!loading && filteredUsers.length > 0 && viewMode === 'table' && (
            <div style={{ 
              backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                  <thead>
                    <tr style={{ 
                      backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
                      borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`
                    }}>
                      <th 
                        style={{ 
                          padding: '14px 16px', 
                          textAlign: 'left',
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                          fontWeight: 600,
                          fontSize: '13px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          userSelect: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleSort('name')}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>Name</span>
                          {sortConfig.key === 'name' && (
                            <ChevronDown size={16} style={{ 
                              transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.2s'
                            }} />
                          )}
                        </div>
                      </th>
                      <th 
                        style={{ 
                          padding: '14px 16px', 
                          textAlign: 'left',
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                          fontWeight: 600,
                          fontSize: '13px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          userSelect: 'none',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => handleSort('status')}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>Status</span>
                          {sortConfig.key === 'status' && (
                            <ChevronDown size={16} style={{ 
                              transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.2s'
                            }} />
                          )}
                        </div>
                      </th>
                      <th 
                        style={{ 
                          padding: '14px 16px', 
                          textAlign: 'left',
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                          fontWeight: 600,
                          fontSize: '13px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          userSelect: 'none',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => handleSort('lastLogoutTime')}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>Abmeldezeit</span>
                          {sortConfig.key === 'lastLogoutTime' && (
                            <ChevronDown size={16} style={{ 
                              transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.2s'
                            }} />
                          )}
                        </div>
                      </th>
                      <th 
                        style={{ 
                          padding: '14px 16px', 
                          textAlign: 'left',
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                          fontWeight: 600,
                          fontSize: '13px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          userSelect: 'none',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => handleSort('lastActivity')}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>Letzte Aktivität</span>
                          {sortConfig.key === 'lastActivity' && (
                            <ChevronDown size={16} style={{ 
                              transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.2s'
                            }} />
                          )}
                        </div>
                      </th>
                      <th 
                        style={{ 
                          padding: '14px 16px', 
                          textAlign: 'left',
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                          fontWeight: 600,
                          fontSize: '13px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          userSelect: 'none',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => handleSort('room')}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span>Raum</span>
                          {sortConfig.key === 'room' && (
                            <ChevronDown size={16} style={{ 
                              transform: sortConfig.direction === 'desc' ? 'rotate(180deg)' : 'rotate(0)',
                              transition: 'transform 0.2s'
                            }} />
                          )}
                        </div>
                      </th>
                      <th 
                        style={{ 
                          padding: '14px 16px', 
                          textAlign: 'left',
                          color: isDarkMode ? '#94a3b8' : '#64748b',
                          fontWeight: 600,
                          fontSize: '13px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}
                      >
                        <span>Notizen</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user, index) => renderUserRow(user, index))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default DailySummaryDashboard;